import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { map, shareReplay, tap } from 'rxjs/operators';
import { AuthResponse } from '../../classes/auth-response';
import { User } from '../../classes/user.class';
import { LocalStorage } from '../../storage.class';
import { ApiService } from '../api/api.service';
import { ConfirmDialogService } from '../confirm-dialog-service/confirm-dialog.service';

@Injectable()
export class AuthService {
    loggedIn = new EventEmitter<boolean>();

    constructor(
        public apiService: ApiService,
        private router: Router,
        private confirmDialogService: ConfirmDialogService
    ) {}

    switchToParent() {
        const parentToken = LocalStorage.getParentUserToken();
        LocalStorage.logoutUser();
        this.refresh(parentToken).subscribe(
            () => {
                this.router.navigate(['/'], { replaceUrl: true });
            },
            () => {
                LocalStorage.unsetParentUser();
                this.router.navigate(['/logout'], { replaceUrl: true });
            }
        );
    }

    switchToProfile(token) {
        const parentToken = LocalStorage.getUserToken();
        const parentUser = LocalStorage.getUser();
        LocalStorage.logoutUser();
        LocalStorage.setParentUser(parentToken, parentUser);
        return this.refresh(token);
    }

    loginAsUser(userId: number) {
        return this.apiService.postCall$<string>('auth/loginAsUser', { user_id: userId });
    }

    loginAsDirkUser(userId: number) {
        return this.apiService.postCall$<string>('auth/loginAsDirkUser', { user_id: userId });
    }

    loginPin(email, pin) {
        return this.apiService
            .postCall$<AuthResponse>('auth/login-code', { pin, email })
            .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
    }

    login(email, password) {
        return this.apiService
            .postCall$<AuthResponse>('auth/login', { email, password })
            .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
    }

    loginEmail(email) {
        return this.apiService.postCall$<boolean>('auth/login-email', { email });
    }

    loginType(email) {
        return this.apiService.postCall$<{
            loginType: string;
        }>('auth/login-type', { email });
    }

    handleMicrosoftCallback(code: string, state: string) {
        return this.apiService
            .getCall$<AuthResponse>('auth/microsoft/callback', {
                code,
                state,
                redirect_origin: window.location.origin,
            })
            .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
    }

    loginWithMicrosoft() {
        return this.apiService.getCallData$<{ auth_url: string }>('auth/microsoft/login', {
            redirect_origin: window.location.origin,
        });
    }

    public logout() {
        if (LocalStorage.getParentUserToken()) {
            this.switchToParent();
        } else {
            this.router.navigateByUrl('/login');
            this.confirmDialogService
                .confirm({
                    title: 'Uitloggen',
                    text: `U wordt uitgelogd... <i class="fas fa-spinner fa-spin"></i>`,
                    confirm: null,
                    cancel: null,
                })
                .then(() => {
                    console.log('LOGOUT A');
                    window.location.reload();
                });
            this.apiService.postCall('auth/logout').then(
                () => {
                    LocalStorage.logoutUser();
                    setTimeout(() => {
                        console.log('LOGOUT B');
                        window.location.reload();
                    });
                },
                () => {
                    LocalStorage.logoutUser();
                    setTimeout(() => {
                        console.log('LOGOUT C');
                        window.location.reload();
                    });
                }
            );
        }
    }

    public getUserDetails() {
        return this.apiService.getCallData$<User>('auth/me').pipe(
            tap(response => LocalStorage.setUser(response)),
            shareReplay(1)
        );
    }

    public requestRestorePassword(email): Promise<any> {
        return this.apiService.postCall('auth/request-restore', { email: email });
    }

    public verifyResetToken(token: string): Promise<Object> {
        return this.apiService.getCall('auth/verify-reset-token', { token });
    }

    public restorePassword(token: string, password: string) {
        return this.apiService
            .postCall$<AuthResponse>('auth/restore', { password, token })
            .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
    }

    public refresh(token?: string) {
        if (token) {
            return this.apiService
                .postCall$<AuthResponse>('auth/refresh', { token })
                .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
        }
        return this.apiService
            .getCall$<AuthResponse>('auth/refresh')
            .pipe(map(response => this.handleResponse(response.data)));
    }

    private handleResponse(authResponse: AuthResponse) {
        if (!!authResponse.access_token) {
            LocalStorage.setUserToken(authResponse.access_token);
            LocalStorage.setUser(authResponse.user);
            LocalStorage.setExpireTimeToken(authResponse.expires_in);
            this.loggedIn.emit(true);
            return authResponse;
        } else {
            this.loggedIn.emit(false);
            this.router.navigate(['logout']);
        }
    }
}
